import {
  AfterViewInit,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '../../services/user.service';
import {
  LeftSidebarMenuService,
  defaultSidebarPersistence,
  SidebarPersistence,
} from './services/left-sidebar-menu.service';
import { TextCode } from '../../models/text-code.interface';
import {
  FormGroup,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SidebarService } from '../../services/dashboard/sidebar.service';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  take,
  filter,
  tap,
} from 'rxjs/operators';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { of } from 'rxjs';
import { PermissionService } from '../../guards/permission/service/permission.service';
import { HeaderMessageStateService } from '../header/services/header-message-state.service';
import { TopLevelMenuItems } from '@enums/toplevel-menu-items.enum';
import { WorkflowType } from 'src/app/graphql/generated';
import { SubTenantsService } from '@services/sub-tenants/sub-tenants.service';
import { PageSidebarMenuComponent } from './components/page-sidebar/page-sidebar-menu.component';
import {
  SidebarWorkflowPanelComponent,
  SidebarWorkflowPanelConfig,
  WorkflowPanelId,
} from './components/sidebar-workflow-panel/sidebar-workflow-panel.component';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AsyncPipe } from '@angular/common';
import { AgilityButtonComponent } from '@agility-components/agility-button/agility-button.component';
import {
  AnalyticsEvents,
  AnalyticsService,
} from 'src/app/analytics/services/analytics/analytics.service';
import {
  MultiSelectModule,
  MultiSelectSelectAllChangeEvent,
} from 'primeng/multiselect';
import { AgilityMultiselectComponent } from '@agility-components/form/agility-multiselect/agility-multiselect.component';

interface MenuAnchor {
  label: string;
  route: string;
  icon: string;
}

function normalizeSidebarPersistencePayload(raw: unknown): SidebarPersistence {
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    return {
      ...defaultSidebarPersistence,
      ...(raw as SidebarPersistence),
    };
  }
  return defaultSidebarPersistence;
}

@Component({
  imports: [
    AgilityButtonComponent,
    AgilityMultiselectComponent,
    AsyncPipe,
    RouterModule,
    AccordionModule,
    ButtonModule,
    TooltipModule,
    MultiSelectModule,
    ReactiveFormsModule,
    FormsModule,
    PageSidebarMenuComponent,
    SidebarWorkflowPanelComponent,
  ],
  selector: 'app-left-sidebar-menu',
  templateUrl: './left-sidebar-menu.component.html',
  styleUrls: ['./left-sidebar-menu.component.scss'],
})
export class LeftSidebarMenuComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  ToplevelMenuItems = TopLevelMenuItems;
  /**
   * When false, the shell is hidden so we do not paint the default-expanded
   * width before async persistence returns (avoids open-then-close flash).
   */
  isSidebarPersistedStateReady = false;
  isExpanded = true;
  isFundingOpen = true;
  isCollateralOpen = true;
  isSettlementOpen = true;

  isTenantUser = this.userService.isTenant;
  isAdminPage = false;
  isFinancingPage = false;
  allSubTenants: TextCode[] = [];
  collateralWorkflowStatusCounts$ = this.permissionService.hasPermission({
    permission: 'COLLATERAL_DASHBOARD',
  })
    ? this.leftSidebarMenuService.getCollateralCounts$()
    : of([]);
  settlementWorkflowStatusCounts$ =
    this.leftSidebarMenuService.getSettlementCounts$();
  fundingWorkflowStatusCounts$ =
    this.leftSidebarMenuService.getFundingCounts$();

  readonly WorkflowType = WorkflowType;

  customTenantMessageVisibility$ =
    this.headerMessageStateService.getCustomTenantMessageVisibility();

  subTenantForm = new FormGroup({
    originatorsDropdown: new FormControl<TextCode[]>([]),
  });

  mainSectionAnchors: MenuAnchor[] = [
    {
      label: 'Funding',
      route: '/secure/funding/funding-pipeline',
      icon: 'pi pi-money-bill',
    },
    {
      label: 'Collateral',
      route: '/secure/collateral/collateral-pipeline',
      icon: 'pi pi-send',
    },
    {
      label: 'Settlement',
      route: '/secure/settlement/settlement-landing',
      icon: 'pi pi-dollar',
    },
  ];

  get workflowPanels(): SidebarWorkflowPanelConfig[] {
    return [
      {
        id: WorkflowType.FUNDING,
        label: 'Funding',
        icon: 'pi pi-money-bill',
        route: '/secure/funding/funding-pipeline',
        toggleAriaLabel: 'Funding pipeline statuses',
        counts$: this.fundingWorkflowStatusCounts$,
        showHighPriorityIndicator: true,
      },
      {
        id: WorkflowType.COLLATERAL,
        label: 'Collateral',
        icon: 'pi pi-send',
        route: '/secure/collateral/collateral-pipeline',
        toggleAriaLabel: 'Collateral pipeline statuses',
        counts$: this.collateralWorkflowStatusCounts$,
        showHighPriorityIndicator: false,
      },
      {
        id: WorkflowType.SETTLEMENT,
        label: 'Settlement',
        icon: 'pi pi-dollar',
        route: '/secure/settlement/settlement-landing',
        toggleAriaLabel: 'Settlement pipeline statuses',
        counts$: this.settlementWorkflowStatusCounts$,
        showHighPriorityIndicator: false,
      },
    ];
  }

  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly userService: UserService,
    private readonly leftSidebarMenuService: LeftSidebarMenuService,
    private readonly sidebarService: SidebarService,
    private readonly router: Router,
    private readonly permissionService: PermissionService,
    private readonly headerMessageStateService: HeaderMessageStateService,
    private readonly originatorsService: SubTenantsService
  ) {
    this.applyRouteFlags(this.router.url);
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => event as NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.applyRouteFlags(event.urlAfterRedirects);
      });
  }

  private applyRouteFlags(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);
    const secureIdx = segments.indexOf('secure');
    const area = secureIdx >= 0 ? segments[secureIdx + 1] : undefined;
    this.isAdminPage = area === 'admin';
    this.isFinancingPage = area === 'financing';
    this.leftSidebarMenuService.setSidebarRouteAreas(
      this.isAdminPage,
      this.isFinancingPage
    );
  }

  ngOnInit() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  ngAfterViewInit(): void {
    /**
     * This is to set the initial value of the originatorsDropdown form control
     * with the selected subtenants from the persistence settings along with
     * the sidebar expansion state and the workflow counts open states
     */
    this.leftSidebarMenuService
      .getSidebarStateFromPersistence$()
      .pipe(
        map(normalizeSidebarPersistencePayload),
        catchError(() => of(defaultSidebarPersistence)),
        switchMap(
          ({
            selectedOriginators,
            isFundingOpen,
            isCollateralOpen,
            isSettlementOpen,
            isSidebarExpanded,
          }) => {
            this.isFundingOpen = isFundingOpen;
            this.isCollateralOpen = isCollateralOpen;
            this.isSettlementOpen = isSettlementOpen;
            this.isExpanded = isSidebarExpanded;

            /** Next tick avoids NG0100 when this runs from ngAfterViewInit */
            queueMicrotask(() => {
              this.isSidebarPersistedStateReady = true;
            });

            if (this.isTenantUser) {
              return this.leftSidebarMenuService.allSubTenants$.pipe(
                tap((originators) => {
                  this.allSubTenants = originators;
                  this.sidebarService.setTotalIMBCount(originators.length);
                }),
                map((originators) => {
                  return this.isTenantUser && selectedOriginators?.length > 0
                    ? originators.filter((subTenant: TextCode) =>
                        selectedOriginators.includes(subTenant.code)
                      )
                    : originators;
                })
              );
            } else {
              this.originatorsService.setInitialSubTenantsFetchComplete(true);

              return of([
                {
                  text: this.userService.originatorId,
                  code: this.userService.originatorId,
                },
              ]);
            }
          }
        ),
        take(1),
        finalize(() =>
          this.leftSidebarMenuService.notifyWorkflowSubTenantSelectionHydrated()
        )
      )
      .subscribe((originators) => {
        this.subTenantForm
          .get('originatorsDropdown')
          ?.setValue(originators, { emitEvent: false });
        this.sidebarService.setSelectedIMBs(originators);
        this.leftSidebarMenuService.updateSidebarPersistenceState({
          selectedOriginators:
            originators?.map((subTenant: TextCode) => subTenant.code) || [],
        });
      });

    this.subTenantForm
      .get('originatorsDropdown')
      ?.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((originators) => {
        this.leftSidebarMenuService.updateSidebarPersistenceState({
          selectedOriginators:
            originators?.map((subTenant: TextCode) => subTenant.code) || [],
        });
        this.sidebarService.setSelectedIMBs(originators || []);
      });
  }

  toggleSidebar(): void {
    this.isExpanded = !this.isExpanded;
    this.leftSidebarMenuService.toggleSidebar();
  }

  togglePanel(panel: WorkflowPanelId): void {
    switch (panel) {
      case WorkflowType.FUNDING:
        this.isFundingOpen = !this.isFundingOpen;
        break;
      case WorkflowType.COLLATERAL:
        this.isCollateralOpen = !this.isCollateralOpen;
        break;
      case WorkflowType.SETTLEMENT:
        this.isSettlementOpen = !this.isSettlementOpen;
        break;
    }

    this.leftSidebarMenuService.updateSidebarPersistenceState({
      isFundingOpen: this.isFundingOpen,
      isCollateralOpen: this.isCollateralOpen,
      isSettlementOpen: this.isSettlementOpen,
    });
  }

  isPanelOpen(panel: WorkflowPanelId): boolean {
    switch (panel) {
      case WorkflowType.FUNDING:
        return this.isFundingOpen;
      case WorkflowType.COLLATERAL:
        return this.isCollateralOpen;
      case WorkflowType.SETTLEMENT:
        return this.isSettlementOpen;
    }
  }

  onMultiSelectAllChange(event: MultiSelectSelectAllChangeEvent): void {
    if (event.checked) {
      this.analyticsService.trackEvent(AnalyticsEvents.UI_CLICK, {
        element_id: 'sidebar-originators-multi-select-all-selected',
        feature: 'left-sidebar-menu',
        label: 'All Originators Selected',
      });
    } else {
      this.analyticsService.trackEvent(AnalyticsEvents.UI_CLICK, {
        element_id: 'sidebar-originators-multi-select-all-unselected',
        feature: 'left-sidebar-menu',
        label: 'All Originators Unselected',
      });
    }
  }

  get sidebarPanelLoaders() {
    return this.leftSidebarMenuService.panelLoaders;
  }

  getPanelLoader(panelId: WorkflowPanelId) {
    return this.sidebarPanelLoaders[panelId];
  }

  get selectedPageName(): TopLevelMenuItems {
    if (this.isFinancingPage) {
      return TopLevelMenuItems.FINANCING;
    }
    return TopLevelMenuItems.ADMIN;
  }
}
